import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertCircle, XCircle, User, Calendar, ArrowRight, Loader2, Ban } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BAIXA_LABELS = {
  serasa: 'SERASA',
  spc_brasil: 'SPC Brasil',
  boa_vista: 'SCPC Boa Vista',
  ieptb_nacional: 'IEPTB Nacional',
  ieptb_sp: 'IEPTB SP'
};

// Mapeamento dos sub-status para exibição com caracteres especiais
const SUB_STATUS_LABELS = {
  'acao_em_processo_protocolo': 'Ação em processo de protocolo',
  'acao_processo_protocolo': 'Ação em processo de protocolo',
  'acao_protocolada': 'Ação protocolada',
  'decisao_concedida': 'Decisão concedida',
  'orgaos_notificados': 'Órgãos notificados'
};

const formatSubStatus = (subStatus) => {
  if (!subStatus) return null;
  return SUB_STATUS_LABELS[subStatus] || subStatus;
};

const STATUS_CONFIG = {
  protocolo: {
    label: 'Protocolo',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    icon: Clock,
    step: 1
  },
  em_processo_baixa: {
    label: 'Em Processo de Baixa',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    icon: AlertCircle,
    step: 2
  },
  concluido: {
    label: 'Concluído',
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: CheckCircle,
    step: 3
  },
  revogada: {
    label: 'Revogada',
    color: 'bg-red-100 text-red-700 border-red-300',
    icon: XCircle,
    step: -1
  },
  cancelado: {
    label: 'Cancelado',
    color: 'bg-gray-200 text-gray-700 border-gray-300',
    icon: Ban,
    step: -1
  }
};

const STEPS = [
  { key: 'protocolo', label: 'Protocolo' },
  { key: 'em_processo_baixa', label: 'Em Baixa' },
  { key: 'concluido', label: 'Concluído' }
];

const ClientPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/portal/${token}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Link inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentStep = () => {
    if (!data) return 0;
    const config = STATUS_CONFIG[data.status];
    return config?.step || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-background to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Link Inválido</h1>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Entre em contato com seu assessor para obter um novo link de acesso.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[data.status] || STATUS_CONFIG.protocolo;
  const StatusIcon = statusConfig.icon;
  const currentStep = getCurrentStep();
  const isRevogada = data.status === 'revogada';
  const isCancelado = data.status === 'cancelado';
  const isTerminated = isRevogada || isCancelado;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center gap-3">
            <img src="/logo.png" alt="SUA Gestão" className="h-10 w-auto" onError={(e) => e.target.style.display = 'none'} />
            <h1 className="text-2xl font-bold text-primary">SUA Gestão</h1>
          </div>
          <p className="text-center text-muted-foreground mt-1 text-sm">Portal de Acompanhamento</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Card do Cliente */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <h2 className="text-xl font-semibold text-foreground">{data.nome}</h2>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Atual */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4">Status do Processo</h3>
              
              <div className={`flex items-center gap-3 p-4 rounded-lg border ${statusConfig.color}`}>
                <StatusIcon className="w-8 h-8" />
                <div>
                  <p className="font-semibold text-lg">{statusConfig.label}</p>
                  {data.sub_status && (
                    <p className="text-sm font-medium mt-1">{formatSubStatus(data.sub_status)}</p>
                  )}
                  {data.ultima_atualizacao && (
                    <p className="text-sm opacity-80">
                      Última atualização: {formatDate(data.ultima_atualizacao)}
                    </p>
                  )}
                </div>
              </div>

              {/* Barra de Progresso (só mostra se processo estiver ativo) */}
              {!isTerminated && (
                <div className="mt-6">
                  <div className="flex justify-between mb-2">
                    {STEPS.map((step, index) => {
                      const isCompleted = currentStep > index + 1;
                      const isCurrent = currentStep === index + 1;
                      return (
                        <div key={step.key} className="flex flex-col items-center flex-1">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                              isCompleted
                                ? 'bg-green-500 text-white'
                                : isCurrent
                                ? 'bg-primary text-white'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {isCompleted ? <CheckCircle className="w-5 h-5" /> : index + 1}
                          </div>
                          <span className={`text-xs mt-1 text-center ${isCurrent ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="relative h-2 bg-muted rounded-full mt-2">
                    <div
                      className="absolute h-2 bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Mensagem para Revogada */}
              {isRevogada && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300 text-sm">
                    Este processo foi revogado e não terá continuidade. Entre em contato com seu assessor para mais informações.
                  </p>
                </div>
              )}

              {/* Mensagem para Cancelado */}
              {isCancelado && (
                <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Este processo foi cancelado. Entre em contato com seu assessor para mais informações.
                  </p>
                </div>
              )}

              {/* Mensagem de Conclusão */}
              {data.status === 'concluido' && data.conclusao_texto && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-green-700 dark:text-green-300 font-medium mb-1">Mensagem de Conclusão:</p>
                  <p className="text-green-600 dark:text-green-400">{data.conclusao_texto}</p>
                </div>
              )}

              {/* Itens de Baixa */}
              {data.status === 'em_processo_baixa' && data.baixa_items && Object.keys(data.baixa_items).length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Detalhamento da Baixa:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(data.baixa_items).map(([org, status]) => (
                      <div key={org} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                        <span>{BAIXA_LABELS[org] || org}</span>
                        <Badge variant={status === 'baixado' ? 'default' : 'secondary'} className="text-xs">
                          {status === 'baixado' ? 'Baixado' : 'Baixando'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Histórico */}
          {data.historico && data.historico.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Histórico de Atualizações
                </h3>
                <div className="space-y-3">
                  {data.historico.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                    >
                      <div className="text-xs text-muted-foreground min-w-[120px]">
                        {formatDate(item.data)}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.status_anterior && (
                          <>
                            <Badge variant="outline" className="text-xs">
                              {item.status_anterior}
                            </Badge>
                            <ArrowRight className="w-3 h-3 text-muted-foreground" />
                          </>
                        )}
                        <Badge className="text-xs bg-primary">
                          {item.novo_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center">
        <p className="text-sm text-muted-foreground">
          © 2026 SUA Assessoria. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
};

export default ClientPortalPage;
